import React, { useEffect, useState } from 'react';
import { Link } from "react-router-dom";
import Table from "../../components/Table/Table";
import { FaFileAlt } from "react-icons/fa";
import { tableConstants } from "./templateTableConstant";
import Loader from '../../components/Loader/Loader';
import { enqueueSnackbar } from 'notistack';
import { getTemplates, deleteTemplate, bulkUpdatePublishDate } from '../../services/TemplateService';
import CardHeader from '../../components/Card/CardHeader';
import Pagination from '../../components/Pagination/Pagination';
import TemplateFilter from './TemplateFilter';
import Button from "../../components/Button/Button";

const initialFilters = { term: '', type: '' };

const TemplateList = () => {
  const [templatesData, setTemplatesData] = useState([]);
  const [isLoader, setIsLoader] = useState(false);
  const [lastPage, setLastPage] = useState(0);
  const [currentPage, setCurrentPage] = useState(0);
  const [filters, setFilters] = useState(initialFilters);
  const [appliedFilters, setAppliedFilters] = useState(initialFilters);

  // Publish date edit state
  const [isEditMode, setIsEditMode] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);

  const permissions = JSON.parse(localStorage.getItem("permissions")) || [];

  const fetchTemplates = async (
    { selected } = { selected: 0 },
    activeFilters = appliedFilters
  ) => {
    const page = selected + 1;
    setCurrentPage(selected);

    try {
      setIsLoader(true);

      const templatesResponse = await getTemplates(page, activeFilters);

      if (templatesResponse.status === 200) {
        const templatesData = templatesResponse.data.data;

        setLastPage(templatesResponse.data.last_page);
        setTemplatesData(templatesData);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoader(false);
    }
  };

  useEffect(() => {
    fetchTemplates({ selected: 0 });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filterChangeHandler = (e) => {
    const { name, value } = e.target;

    setFilters((prev) => ({
      ...prev,
      [name]: value
    }));
  };

  const hasUnsavedChanges = templatesData.some(
    (template) => template.is_updated
  );

  const handlePageChange = (page) => {
    if (isEditMode && hasUnsavedChanges) {
      enqueueSnackbar(
        "Update or cancel your publish date changes before changing page",
        { variant: "info" }
      );
      return;
    }

    fetchTemplates(page);
  };

  const handleSearch = ({ selected }) => {
    setIsEditMode(false);

    setAppliedFilters(filters);

    fetchTemplates(
      { selected },
      filters
    );
  };

  const handleReset = () => {
    setIsEditMode(false);

    setFilters(initialFilters);
    setAppliedFilters(initialFilters);

    fetchTemplates(
      { selected: 0 },
      initialFilters
    );
  };

  const toggleEditMode = () => {
    if (isEditMode) {
      // Discard unsaved changes by reloading current page
      fetchTemplates({
        selected: currentPage
      });
    }

    setIsEditMode((prev) => !prev);
  };

  const publishDateChangeHandler = (e, id) => {
    const { name, value } = e.target;

    setTemplatesData((prev) =>
      prev.map((template) =>
        template.id === id
          ? {
              ...template,
              [name]: value,
              is_updated: true
            }
          : template
      )
    );
  };

  const updateBulkPublishDateHandler = async () => {
    const payload = templatesData
      .filter((template) => template.is_updated)
      .map((template) => ({
        template_id: template.id,
        publish_date: template.publish_date
      }));

    if (!payload.length) {
      enqueueSnackbar(
        "No changes detected",
        { variant: "info" }
      );
      return;
    }

    if (payload.some((template) => !template.publish_date)) {
      enqueueSnackbar(
        "Publish date cannot be empty",
        { variant: "warning" }
      );
      return;
    }

    try {
      setIsUpdating(true);

      const response = await bulkUpdatePublishDate({
        templates: payload
      });

      if (response.status === 200) {
        enqueueSnackbar(
          response.data?.message?.success ||
            "Publish date updated successfully",
          { variant: "success" }
        );

        setIsEditMode(false);

        fetchTemplates({
          selected: currentPage
        });
      }
    } catch (error) {
      enqueueSnackbar(
        error.response?.data?.message?.error ||
          "Bulk update failed",
        { variant: "error" }
      );
    } finally {
      setIsUpdating(false);
    }
  };

  const templateDelete = async (id) => {
    try {
      const response = await deleteTemplate(id);

      if (response.status === 200) {
        enqueueSnackbar(
          response.data.message.success,
          { variant: 'success' }
        );

        fetchTemplates({
          selected: currentPage
        });
      }
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <div className="card">
      <CardHeader
        title="Templates"
        icon={<FaFileAlt />}
        button={
          <React.Fragment>
            {permissions.includes("template.update") &&
              templatesData.length > 0 && (
                <button
                  type="button"
                  className={`btn ${
                    isEditMode
                      ? 'btn-secondary'
                      : 'btn-success'
                  } mx-2`}
                  onClick={toggleEditMode}
                >
                  {isEditMode
                    ? "Cancel Edit"
                    : "Edit Publish Date"}
                </button>
              )}

            {permissions.includes("template.create") && (
              <Link
                className="btn btn-primary"
                to="/template/create"
              >
                Add Template
              </Link>
            )}
          </React.Fragment>
        }
      />

      <div className="card-body evaluating-table">
        <TemplateFilter
          term={filters.term}
          type={filters.type}
          onChangeHandler={filterChangeHandler}
          handleSearch={handleSearch}
          handleReset={handleReset}
        />

        {isLoader ? (
          <Loader />
        ) : templatesData && templatesData.length > 0 ? (
          <React.Fragment>
            <Table
              cols={tableConstants(
                templateDelete,
                permissions,
                isEditMode,
                publishDateChangeHandler
              )}
              data={templatesData}
              hoverable={true}
              striped={true}
            />

            {isEditMode && (
              <div className="d-flex justify-content-end my-2">
                <Button
                  variant="primary"
                  type="button"
                  onClick={updateBulkPublishDateHandler}
                  disabled={isUpdating}
                >
                  {isUpdating
                    ? "Updating..."
                    : "Update"}
                </Button>
              </div>
            )}

            <Pagination
              totalPage={lastPage}
              currentPage={currentPage}
              handlePageChange={handlePageChange}
            />
          </React.Fragment>
        ) : (
          <div className="d-flex justify-content-center">
            No data found
          </div>
        )}
      </div>
    </div>
  );
};

export default TemplateList;